import { ChangeEvent, useEffect, useState } from "react";

import {
  Box,
  Grid,
  alpha,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockIcon from "@mui/icons-material/Lock";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";

import moment from "moment";

import { IPlanSubscriptions } from "../../../ModalSubscriptions";
import { AppTextField, FormCore } from "src/Pages/components";
import { ButtonCore } from "src/Pages/components/ButtonCore/ButtonCore";
import { LineTempDescriptions } from "./LineTempDescriptions";
import { cpfOurCnpj } from "src/shared/Utils/MaskCpfCnpj";
import {
  apenasNumeros,
  FormatMoney,
  handleChangeExpiryDate,
  handleFormatCreditCard,
} from "src/shared/Utils/FormatMoney";
import { api } from "src/shared/setup/API/api";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";
import { CardAlertsMessages } from "src/Pages/components/CardAlertsMesages/CardAlertsMesages";
import { UseFormAbandonment } from "../../../Hooks/UseFormAbandonment";

interface IInputCreateSubscription {
  name: string;
  number: string;
  expiryDate: string;
  cvc: string;
  cpf: string;

  zipCode: string;
  numberAddress: string;
}

interface IResponseApi {
  message: string;
  recuse: boolean;
  success: boolean;
  errors: string;
  messageAssas: {
    code: string;
    description: string;
  }[];
}

interface CardCreditSubscriptionProps {
  setStep: (step: number) => void;
  planSelected: IPlanSubscriptions;
}

export const CardCreditSubscription = ({
  setStep,
  planSelected,
}: CardCreditSubscriptionProps) => {
  const { setUserPerfil } = useAuthContext();
  const { handleSubmit } = UseFormAbandonment();

  useEffect(() => {
    handleSubmit({ event: "CHECKOUT" });
  }, []);

  const [errors, setErrors] = useState<string[]>([]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [dataCard, setDataCard] = useState<IInputCreateSubscription>({
    name: "",
    number: "",
    expiryDate: "",
    cvc: "",
    cpf: "",
    zipCode: "",
    numberAddress: "",
  });

  const trialDays = 10;
  const reminderDate = moment()
    .add(trialDays - 10, "days")
    .format("DD [de] MMMM [de] YYYY");

  const billingDate = moment()
    .add(trialDays, "days")
    .format("DD [de] MMMM [de] YYYY");

  const handleAddSubscription = () => {
    const data = {
      idPlan: planSelected.id,
      cpf: apenasNumeros(dataCard.cpf) || "",
      creditCard: {
        card: apenasNumeros(dataCard.number) || "",
        holderName: dataCard.name?.trim() || "",
        number: apenasNumeros(dataCard.number) || "",
        expiryYear: dataCard.expiryDate.split("/")[1] || "",
        expiryMonth: dataCard.expiryDate.split("/")[0] || "",
        cvc: dataCard.cvc || "",
      },
      zipCode: apenasNumeros(dataCard.zipCode) || "",
      number: dataCard.numberAddress || "",
    };

    const errors = [];

    if (data.creditCard.holderName.length < 3) {
      errors.push("Nome do titular inválido");
    }

    if (data.cpf.length !== 11) {
      errors.push("CPF inválido");
    }

    if (data.creditCard.number.length !== 16) {
      errors.push("Número do cartão inválido");
    }

    if (data.creditCard.cvc.length < 3 || data.creditCard.cvc.length > 4) {
      errors.push("Código de segurança inválido");
    }

    if (data.zipCode.length < 5 || data.zipCode.length > 8) {
      errors.push("CEP inválido");
    }

    if (!data.number.length) {
      errors.push("Número inválido");
    }

    if (errors.length > 0) {
      setErrors(errors);
      return;
    }

    setLoadingSubmit(true);
    api
      .post<IResponseApi>("/checkout", data)
      .then((res) => {
        setLoadingSubmit(false);

        if (res.data.recuse) {
          return setErrors(
            res.data?.messageAssas.map((item) => item?.description)
          );
        }

        setStep(4);
        api.post(`/auth/sessions`).then(({ data }) => {
          setUserPerfil(data);
        });
      })
      .catch((error) => {
        setErrors([
          error?.response?.data?.message || "Erro ao processar pagamento.",
        ]);
      })
      .finally(() => setLoadingSubmit(false));
  };

  const handleChange =
    (
      prop: keyof IInputCreateSubscription,
      option?: "cpf" | "phone" | "codeCVC" | "expiryDate" | "cardNumber"
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (option === "cardNumber") {
        setDataCard((eventPrev) => ({
          ...eventPrev,
          [prop]: handleFormatCreditCard(event.target.value),
        }));
      } else if (option === "expiryDate") {
        setDataCard((eventPrev) => ({
          ...eventPrev,
          [prop]: handleChangeExpiryDate(event.target.value),
        }));
      } else if (option === "codeCVC") {
        if (event.target.value.length <= 4) {
          setDataCard((eventPrev) => ({
            ...eventPrev,
            [prop]: apenasNumeros(event.target.value),
          }));
        }
      } else if (option === "cpf") {
        if (event.target.value.length <= 14) {
          setDataCard((eventPrev) => ({
            ...eventPrev,
            [prop]: cpfOurCnpj({ text: event.target.value, type: "cpf" }),
          }));
        }
      } else {
        setDataCard((eventPrev) => ({
          ...eventPrev,
          [prop]: event.target.value,
        }));
      }
    };

  return (
    <Grid container height="100%">
      <Grid item xs={12} sm={6} sx={{ padding: { xs: "24px", md: "24px" } }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <IconButton
              onClick={() => setStep(2)}
              sx={{ color: "text.primary", mr: { xs: 0.5, md: 1 } }}
            >
              <ArrowBackIosNewIcon fontSize="small" sx={{ ml: "-3px" }} />
            </IconButton>

            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: { xs: "16px", md: "20px" } }}
            >
              Experimente o Correctors grátis
            </Typography>
          </Box>

          <FormCore sxStyle={{ m: 0 }}>
            <Box
              sx={{
                border: (theme) =>
                  `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
                borderRadius: "8px",
                p: 2,
                mb: 3.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CreditCardIcon sx={{ mr: 1 }} fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Cartão de crédito
                </Typography>
              </Box>

              {errors.length > 0 && (
                <CardAlertsMessages messages={errors} isFirst={true} />
              )}

              <AppTextField
                label="Titular do cartão"
                value={dataCard.name}
                onChange={handleChange("name")}
                fullWidth
                placeholder="Titular do cartão"
                sxStyle={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
              />
              <AppTextField
                label="CPF"
                value={dataCard.cpf}
                onChange={handleChange("cpf", "cpf")}
                fullWidth
                placeholder="CPF"
                sxStyle={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
              />

              <AppTextField
                label="Número do cartão"
                value={dataCard.number}
                onChange={handleChange("number", "cardNumber")}
                fullWidth
                placeholder="Número do cartão"
                sxStyle={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCardIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
              />

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <AppTextField
                    label="Data de expiração"
                    value={dataCard.expiryDate}
                    onChange={handleChange("expiryDate", "expiryDate")}
                    fullWidth
                    placeholder="11/2029"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AppTextField
                    label="Código segurança (CVC)"
                    value={dataCard.cvc}
                    onChange={handleChange("cvc", "codeCVC")}
                    fullWidth
                    placeholder="789"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AppTextField
                    label="CEP de cobrança"
                    value={dataCard.zipCode}
                    onChange={handleChange("zipCode")}
                    fullWidth
                    placeholder="01001000"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalPostOfficeIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AppTextField
                    label="Número da residência"
                    value={dataCard.numberAddress}
                    onChange={handleChange("numberAddress")}
                    fullWidth
                    placeholder="52B"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalPostOfficeIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                A pagar em {billingDate}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {FormatMoney(Math.round(planSelected.amount * 100))}
                {/* {planSelected.price.replace("/mês", "")} */}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <strong style={{ color: "#35af2a" }}>
                  A pagar hoje ({trialDays} dias grátis)
                </strong>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <strong style={{ color: "#35af2a" }}>R$0</strong>
              </Typography>
            </Box>

            <ButtonCore
              variant="contained"
              fullWidth
              disabled={loadingSubmit}
              onClick={handleAddSubscription}
              // loading={loadingSubmit}
              type="submit"
              sx={{ bgcolor: "#8B3DFF", py: 1.3 }}
              title={`Começar meu teste grátis de ${trialDays} dias`}
            />
          </FormCore>
        </Box>
      </Grid>

      {/* Right Side: Information */}
      <Grid
        item
        xs={0}
        sm={6}
        sx={{
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          alignItems: "center",
          padding: "32px",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#242424" : "#f8f9fa",
        }}
      >
        <LineTempDescriptions
          trialDays={trialDays}
          reminderDate={reminderDate}
          billingDate={billingDate}
        />
      </Grid>
    </Grid>
  );
};
